/**
 * Which persona the mocked `/me` endpoint answers as.
 *
 * Module-level state rather than a React context: the one reader is the
 * `/me` handler in `test/mocks/handlers.ts`, which runs inside the MSW
 * worker, entirely outside React. `MockPersonaSwitch` in `App.tsx` is the one
 * writer — it also calls `useMe`'s `retry` after every change, since flipping
 * this value alone does not refetch anything.
 *
 * Meaningless, and never rendered, once `VITE_USE_MOCKS` is off: there is no
 * mock `/me` handler to read it, and the real API decides who you are.
 */
/**
 * **Three, and the third one changes agency rather than role.**
 *
 * `admin` and `member` are two people at the sheriff's department, so switching
 * between them varies a *role* over one fixture graph. `prosecutor` is the
 * 16th JDA's admin — the agency that convenes First Appearances — and it is here
 * because CON-24 has no expression from a single seat: an `own`-scope member and
 * an `all`-scope one looking at the same folder is a fact about **two** readers,
 * and so is "the owner changed my scope". `MockPlanSwitch`'s own header
 * anticipated this axis when it said an agency's state "belongs to
 * `MockPersonaSwitch`'s side of the fixture if it is ever wanted".
 *
 * **It is defined for the Files and Spaces surfaces and for nothing else.** The
 * handlers branch on it in `/me`, `GET /spaces`, and the joint docket's folders
 * and files; `/users`, `/audit` and `/storage` still answer the sheriff's graph,
 * so switching to this persona and opening Settings shows another agency's data
 * under this one's name. That is a known and bounded lie rather than an
 * oversight — modelling four agencies' rosters and logs would double this file
 * to serve one journey — and it is stated here because nothing on screen says
 * it. Do not assert against a non-Files surface under this persona.
 */
export type MockPersona = 'admin' | 'member' | 'prosecutor';

let current: MockPersona = 'admin';

export function getMockPersona(): MockPersona {
  return current;
}

export function setMockPersona(persona: MockPersona): void {
  current = persona;
}

/** Test-only: no test may leave the persona changed for the next one. */
export function resetMockPersona(): void {
  current = 'admin';
}
